import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import FilterPopover from '../ui/FilterPopover';
import {
  ASSIGNMENTS_MARK_PACKED_EVENT, ASSIGNMENT_DELETE_EVENT, ASSIGNMENT_PACK_ALL_EVENT, ASSIGNMENT_SAVE_EVENT,
  setAssignmentsBar, useAssignmentsBar,
} from '../../hooks/useAssignmentsBar';
import { useRole } from '../../hooks/useRole';
import { PACKING_OPTIONS } from '../../utils/asoebiAssignments';
import type { AssignmentPacking } from '../../types/ui/AsoebiAssignment';

export const ASSIGNMENT_NEW_PATH = '/asoebi/assignments/new';
const send = (name: string) => window.dispatchEvent(new CustomEvent(name));

/**
 * The Assignments list's actions in the Header's bar (the same pattern as
 * AsoebiOrdersBar): the Archived switch, one Packing filter, Mark packed for the
 * ticked rows and New Assignment. The list reads the same store to filter itself.
 */
export const AssignmentsListBar: React.FC = () => {
  const navigate = useNavigate();
  const bar = useAssignmentsBar();
  const { canEdit } = useRole();
  return (
    <>
      <div className="header-filter-group">
        <ArchivedToggle checked={bar.archived} onChange={archived => setAssignmentsBar({ archived, chosen: 0 })} />
      </div>
      <FilterPopover
        selects={[{
          key: 'packing',
          label: 'Packing',
          options: [{ value: '', label: 'All' }, ...PACKING_OPTIONS],
          value: bar.packing,
          defaultValue: '',
          onChange: value => setAssignmentsBar({ packing: value as AssignmentPacking | '' }),
        }]}
      />
      {canEdit && !bar.archived && (
        <button
          type="button"
          className="header-action-btn header-action-btn--labelled"
          disabled={bar.chosen === 0}
          title={bar.chosen === 0 ? 'Tick the assignments to pack first' : 'Mark the ticked assignments packed'}
          onClick={() => send(ASSIGNMENTS_MARK_PACKED_EVENT)}
        >
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">{bar.chosen > 0 ? `Mark packed (${bar.chosen})` : 'Mark packed'}</span>
        </button>
      )}
      {canEdit && !bar.archived && (
        <button type="button" className="header-action-btn header-action-btn--labelled primary" title="New Assignment" onClick={() => navigate(ASSIGNMENT_NEW_PATH)}>
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Assignment</span>
        </button>
      )}
    </>
  );
};

/** The open assignment's actions: Mark all packed, Delete, Save / Create. */
export const AssignmentEditorBar: React.FC = () => {
  const { editor } = useAssignmentsBar();
  return (
    <>
      {editor.canMarkPacked && (
        <button type="button" className="header-action-btn header-action-btn--labelled" title="Mark all packed" onClick={() => send(ASSIGNMENT_PACK_ALL_EVENT)}>
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Mark all packed</span>
        </button>
      )}
      {editor.canDelete && (
        <button type="button" className="header-action-btn header-action-btn--labelled header-action-btn--danger" title="Delete" onClick={() => send(ASSIGNMENT_DELETE_EVENT)}>
          <Icon name="trash" size={16} />
          <span className="header-action-btn__label">Delete</span>
        </button>
      )}
      {editor.canSave && (
        <button
          type="button"
          className="header-action-btn header-action-btn--labelled primary"
          disabled={editor.saving || (!editor.creating && !editor.dirty)}
          title={editor.creating ? 'Create assignment' : 'Save'}
          onClick={() => send(ASSIGNMENT_SAVE_EVENT)}
        >
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">{editor.saving ? 'Saving…' : editor.creating ? 'Create assignment' : 'Save'}</span>
        </button>
      )}
    </>
  );
};
